import * as React from 'react'

import SchoolSelector from './schoolSelector'
import { School, CanvasInstance, } from './shared'

import { requestPost, requestPut, requestDelete, } from '../../../../modules/request';
import { Input, } from '../../../Shared/index'

type InputElement = React.ChangeEvent<HTMLInputElement>

interface CanvasIntegrationFormProps {
  schools: School[];
  success: () => void;
  existingIntegration?: CanvasInstance;
  close?: () => void;
}

export const CANVAS_INTEGRATIONS_PATH = '/canvas_instances'

const isValidURL = (url: string): boolean => {
  try {
    const parsedURL = new URL(url);
    return ['http:', 'https:'].includes(parsedURL.protocol);
  } catch (error) {
    return false;
  }
}

export const FALLBACK_ERROR_MESSAGE = 'Something went wrong saving this integration. Please try again.'

const CanvasIntegrationForm = ({ schools, existingIntegration=null, close=null, success, }: CanvasIntegrationFormProps) => {
  const [clientId, setClientId] = React.useState(existingIntegration?.client_id || '')
  const [clientSecret, setClientSecret] = React.useState(existingIntegration?.client_secret || '')
  const [instanceUrl, setInstanceUrl] = React.useState(existingIntegration?.url || '')
  const [selectedSchoolIds, setSelectedSchoolIds] = React.useState(existingIntegration?.school_ids || [])
  const [errorsByAttribute, setErrorsByAttribute] = React.useState<Record<string, string[]>>({})
  const [timesSubmitted, setTimesSubmitted] = React.useState(0)

  const urlError = errorsByAttribute.url?.join(' ')
  const otherErrors = Object.entries(errorsByAttribute)
    .filter(([attribute]) => attribute !== 'url')
    .flatMap(([, messages]) => messages)

  function validInput() {
    return Boolean(
      instanceUrl.length
      && isValidURL(instanceUrl)
      && clientId.length
      && clientSecret.length
      && selectedSchoolIds.length
    )
  }

  function closeOrReset() {
    if (close) {
      close()
    } else {
      setClientId('')
      setClientSecret('')
      setInstanceUrl('')
      setSelectedSchoolIds([])
      setErrorsByAttribute({})
    }
  }

  function handleRequestError(body) {
    const errors = body?.errors
    const errorsPresent = errors && typeof errors === 'object' && Object.keys(errors).length
    setErrorsByAttribute(errorsPresent ? errors : { base: [FALLBACK_ERROR_MESSAGE] })
  }

  function handleChangeInstanceUrl(e: InputElement) { setInstanceUrl(e.target.value) }
  function handleChangeClientId(e: InputElement) { setClientId(e.target.value) }
  function handleChangeClientSecret(e: InputElement) { setClientSecret(e.target.value) }

  function handleDelete() {
    return requestDelete(
      `${CANVAS_INTEGRATIONS_PATH}/${existingIntegration.id}`,
      {},
      () => {
        closeOrReset()
        success()
      },
      handleRequestError
    )
  }

  function handleSubmit(e) {
    e.preventDefault()

    if (!validInput()) { return }

    setErrorsByAttribute({})
    setTimesSubmitted(timesSubmitted + 1)

    const params = {
      canvas_config: {
        client_id: clientId,
        client_secret: clientSecret
      },
      canvas_instance: {
        url: instanceUrl
      },
      canvas_instance_schools: {
        school_ids: selectedSchoolIds
      }
    }

    if (existingIntegration) {
      return requestPut(
        `${CANVAS_INTEGRATIONS_PATH}/${existingIntegration.id}`,
        params,
        () => {
          closeOrReset()
          success()
        },
        handleRequestError
      )
    }

    return requestPost(
      CANVAS_INTEGRATIONS_PATH,
      params,
      () => {
        closeOrReset()
        success()
      },
      handleRequestError
    )
  }

  let deleteButtonOrEmptySpan = <span />

  if (existingIntegration) {
    deleteButtonOrEmptySpan = (
      <button
        className="quill-button contained primary medium red focus-on-light"
        onClick={handleDelete}
        type="button"
      >
        Delete
      </button>
    )
  }

  const formName = `${existingIntegration ? 'Edit' : 'New'} Canvas Integration`

  return (
    <form aria-label={formName} className="canvas-integration-form" name={formName}>
      <h2 id="canvasIntegrationModalTitle">{formName}</h2>
      <Input
        error={urlError}
        handleChange={handleChangeInstanceUrl}
        id="canvas-instance-url"
        label="Canvas Instance URL"
        timesSubmitted={timesSubmitted}
        type="url"
        value={instanceUrl}
      />
      <Input
        handleChange={handleChangeClientId}
        id="client-id"
        label="Client ID"
        value={clientId}
      />
      <Input
        handleChange={handleChangeClientSecret}
        id="client-secret"
        label="Client Secret"
        value={clientSecret}
      />
      <SchoolSelector
        schools={schools}
        selectedSchoolIds={selectedSchoolIds}
        setSelectedSchoolIds={setSelectedSchoolIds}
      />
      {otherErrors.map(message => (
        <p className="error-text" key={message} role="alert">{message}</p>
      ))}
      <div className="button-wrapper">
        {deleteButtonOrEmptySpan}
        <div>
          <button
            className="quill-button medium outlined grey focus-on-light"
            onClick={closeOrReset}
            type="button"
          >
            Cancel
          </button>
          <button
            className={`quill-button medium contained green focus-on-light ${validInput() ? '' : 'disabled'}`}
            disabled={!validInput()}
            onClick={handleSubmit}
            type="submit"
          >
            Submit
          </button>
        </div>
      </div>
    </form>
  )
}

export default CanvasIntegrationForm
